import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useQuery } from '@sopranodesign/pulse-hooks';
import {
  Panel,
  StretchPanel,
  Flex,
  Span,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';

import { MetricContext } from '../../Contexts/index';
import types from '../../Components/Types/index';
import { getSharedMetric } from '../Dashboard/api/index';

const SharedMetric = ({ token, metric, start, end, compare, onSettled }) => {
  const [page, setPage] = useState(1);

  const [params, setParams] = useState({});

  const order = useOrderableTableReducer({ id: `shared-${metric.key}` });

  const type = types[metric.type];

  const query = useQuery({
    queryKey: [
      'shared-metric',
      token,
      metric.id,
      { page, ...params, ...order.params }
    ],
    queryFn: ({ queryKey }) =>
      getSharedMetric({ token, id: metric.id, params: queryKey[3] }),
    enabled: Boolean(type),
    ...type?.query
  });

  const settled = !type || query.isSuccess || query.isError;

  useEffect(() => {
    if (settled) {
      onSettled(metric.id);
    }
  }, [settled, metric.id, onSettled]);

  // A metric type this build doesn't know how to draw is skipped rather than breaking the page.
  if (!type) {
    return null;
  }

  const layout = {
    gridColumn: `span ${type.columns}`,
    gridRow: `auto / span ${type.rows}`,
    minHeight: '8rem'
  };

  if (query.isError) {
    return (
      <Panel data-print-avoid-break style={layout}>
        <Flex col middle tall role="alert">
          <Span>{metric.name}</Span>
          <Span light>This metric couldn&apos;t be loaded.</Span>
        </Flex>
      </Panel>
    );
  }

  if (!query.data) {
    return <Panel loading loadingItems={1} style={layout} />;
  }

  const { Component: MetricType } = type;

  return (
    <MetricContext.Provider
      value={{
        metric: query.data,
        page,
        setPage,
        isLoading: query.isFetching,
        params,
        setParams,
        order,
        start,
        end,
        compare,
        editing: false
      }}>
      <StretchPanel
        data-print-avoid-break
        style={{ ...layout, width: '100%', display: 'flex' }}>
        <Flex fat="true" col tall middle>
          <MetricType />
        </Flex>
      </StretchPanel>
    </MetricContext.Provider>
  );
};

SharedMetric.propTypes = {
  token: PropTypes.string.isRequired,
  metric: PropTypes.shape({
    id: PropTypes.number.isRequired,
    key: PropTypes.string.isRequired,
    name: PropTypes.string,
    type: PropTypes.string.isRequired
  }).isRequired,
  start: PropTypes.string,
  end: PropTypes.string,
  compare: PropTypes.bool,
  onSettled: PropTypes.func.isRequired
};

export default SharedMetric;
